import type { AiCompanionSessionCandidate } from "@simbelapp/order-sdk";

/**
 * When a candidate session ran, as the picker cards date it. Numeric dates rather than the spelled
 * out `attendanceSessionDatesLabel`: the cards are compared side by side, not read as a sentence.
 */
export function attendanceCandidateDateLabel(
  candidate: AiCompanionSessionCandidate,
  formatDate: (date: string | Date) => string,
): string | null {
  if (candidate.startDate && candidate.endDate) {
    return `du ${formatDate(candidate.startDate)} au ${formatDate(candidate.endDate)}`;
  }
  // A session missing one of its bounds is still worth dating from the other.
  if (candidate.startDate) return `à partir du ${formatDate(candidate.startDate)}`;
  if (candidate.endDate) return `jusqu'au ${formatDate(candidate.endDate)}`;

  return null;
}

/**
 * Everything but the chosen card goes inert as soon as the choice is sent — a resumed run takes no
 * second answer. The chosen one stays live so it keeps the accent that says it was chosen. Staging
 * a pick locks nothing: until the button confirms it, the admin may still land on another card.
 */
export function isAttendanceCandidateLocked(
  candidate: AiCompanionSessionCandidate,
  pickedSessionId: string | null,
  disabled = false,
): boolean {
  if (disabled) return true;

  return !!pickedSessionId && candidate.sessionId !== pickedSessionId;
}

/** The sentence the question always ends on, whether or not the training can be named. */
const CHOICE_INSTRUCTION = "Sélectionner ci-dessous la session à mettre à jour :";

/** "Excel — Niveau perfectionnement — Cegos", the way the question names a training. */
function candidateSubject(candidate: AiCompanionSessionCandidate): string | null {
  const title = candidate.trainingTitle?.trim();
  if (!title) return null;

  const partner = candidate.trainingPartner?.trim();

  return partner ? `${title} — ${partner}` : title;
}

/**
 * The training and provider at least two candidates share, `null` when none is. A pair carried by a
 * single session is deliberately not named: "Plusieurs sessions correspondent à …" would then be
 * false, and the date sweep does return lists where every candidate is a different training.
 */
function sharedCandidateSubject(candidates: AiCompanionSessionCandidate[]): string | null {
  const counts = new Map<string, number>();

  for (const candidate of candidates) {
    const subject = candidateSubject(candidate);
    if (subject) counts.set(subject, (counts.get(subject) ?? 0) + 1);
  }

  let shared: string | null = null;
  let sharedCount = 1;
  // Insertion order breaks ties, so an equally shared pair keeps the backend's own ranking.
  for (const [subject, count] of counts) {
    if (count > sharedCount) {
      shared = subject;
      sharedCount = count;
    }
  }

  return shared;
}

/**
 * The question above the picker. Written here rather than read off the run: the payload reports the
 * sessions the detection hesitates between, never what it read on the sheet, so the training is
 * named from the candidates themselves. A single candidate is not an ambiguity — the run is parked
 * on it because it is not the session on screen, which the warning says — so it only gets the
 * instruction.
 */
export function attendanceSessionChoicePrompt(candidates: AiCompanionSessionCandidate[]): string {
  if (candidates.length < 2) return CHOICE_INSTRUCTION;

  const subject = sharedCandidateSubject(candidates);

  return subject
    ? `Plusieurs sessions correspondent à ${subject}, et le document ne permet pas d'en identifier une seule. ${CHOICE_INSTRUCTION}`
    : `Plusieurs sessions correspondent au document, qui ne permet pas d'en identifier une seule. ${CHOICE_INSTRUCTION}`;
}
